/**
 * Transaction Table Component
 * Displays financial transactions in a table format
 */

import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  Chip,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { TableCell, TableHeaderCell, TableRow, TableContainer } from '../../../components/ui';
import { CopyableText } from '../../../components/common';
import type { FinancialTransaction } from '../../../types/finance';
import { formatDateTime } from '../../../utils/dateUtils';

interface TransactionTableProps {
  transactions: FinancialTransaction[];
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount));
};

const getTransactionTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    asset_payment: 'Asset Payment',
    booking_fee: 'Booking Fee',
    platform_fee: 'Platform Fee',
    ghost_payout: 'Ghost Payout',
    refund: 'Refund',
  };
  return labels[type] || type;
};

const getTransactionTypeColor = (type: string, theme: any): string => {
  switch (type) {
    case 'asset_payment':
      return theme.palette.success.main;
    case 'booking_fee':
      return theme.palette.info.main;
    case 'platform_fee':
      return theme.palette.info.light;
    case 'ghost_payout':
      return theme.palette.warning.main;
    case 'refund':
      return theme.palette.error.main;
    default:
      return theme.palette.grey[500];
  }
};

/** Status colors for backend PaymentStatus values; display them as-is. */
const getStatusColor = (status: string, theme: any): string => {
  switch (status) {
    case 'Settled':
      return theme.palette.success.main;
    case 'SettlementSubmitted':
      return theme.palette.info.main;
    case 'Authorized':
    case 'Expired':
      return theme.palette.warning.main;
    case 'Failed':
    case 'Voided':
      return theme.palette.error.main;
    case 'Refunded':
      return theme.palette.grey[500];
    default:
      return theme.palette.text.secondary;
  }
};

export const TransactionTable: React.FC<TransactionTableProps> = ({ transactions }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  if (transactions.length === 0) {
    return (
      <Box sx={{ padding: theme.spacing(4), textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          No transactions found
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table sx={{ minWidth: 1200 }}>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Transaction ID</TableHeaderCell>
            <TableHeaderCell>Mission Code</TableHeaderCell>
            <TableHeaderCell>Mission Title</TableHeaderCell>
            <TableHeaderCell>Type</TableHeaderCell>
            <TableHeaderCell>Amount</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell>Date</TableHeaderCell>
            <TableHeaderCell>Asset</TableHeaderCell>
            <TableHeaderCell>Ghost</TableHeaderCell>
            <TableHeaderCell>Actions</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map((transaction) => (
            <TableRow key={transaction.id}>
              <TableCell sx={{ fontWeight: 500 }}>
                {transaction.transactionId}
              </TableCell>
              <TableCell>
                {(transaction.missionCode) ? (
                  <CopyableText
                    text={transaction.missionCode}
                    onTextClick={() => navigate(`/missions/${transaction.missionId ?? transaction.missionCode}`)}
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 400,
                      fontSize: theme.typography.body2.fontSize,
                    }}
                  />
                ) : (
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    N/A
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ maxWidth: 200 }}>
                <Typography variant="body2" noWrap title={transaction.missionTitle}>
                  {transaction.missionTitle ?? '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={getTransactionTypeLabel(transaction.type)}
                  size="small"
                  sx={{
                    backgroundColor: getTransactionTypeColor(transaction.type, theme),
                    color: theme.palette.common.white,
                    fontWeight: 500,
                    fontSize: theme.typography.caption.fontSize,
                  }}
                />
              </TableCell>
              <TableCell
                sx={{
                  color: transaction.amount >= 0
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                  fontWeight: 600,
                }}
              >
                {transaction.amount >= 0 ? '+' : '-'}{formatCurrency(transaction.amount)}
              </TableCell>
              <TableCell>
                <Chip
                  label={transaction.status}
                  size="small"
                  sx={{
                    backgroundColor: getStatusColor(transaction.status, theme),
                    color: theme.palette.common.white,
                    fontWeight: 500,
                    fontSize: theme.typography.caption.fontSize,
                  }}
                />
              </TableCell>
              <TableCell sx={{ color: theme.palette.text.secondary }}>
                {formatDateTime(transaction.date)}
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                    {transaction.assetName || 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    {transaction.assetEmail}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                    {transaction.ghostName || 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    {transaction.ghostEmail}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate(`/finance/${transaction.id}`)}
                  sx={{
                    color: theme.palette.primary.main,
                    cursor: 'pointer',
                    textDecoration: 'none',
                    fontSize: theme.typography.body2.fontSize,
                    fontWeight: 400,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  View
                </Link>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
